import * as Sentry from '@sentry/nuxt'

// @sentry/nuxt 以 `vue:error` hook 上報 Vue 崩潰，但其 reportNuxtError 只帶 info 字串（上游原始碼標註
// `todo: add component name and trace`），且刻意以 attachErrorHandler: false 關閉 @sentry/vue 自己的
// error handler，因此 vue-error 事件既無第一方 frame 也無元件資訊 —— CLIENT-PWA-SSR-C1~C5 這類 runtime
// patch/unmount 崩潰的 stacktrace 全在 @vue/runtime-core 內，無法定位到出錯元件（issue #322）。
// 補強方式：於 vue:error 取得 instance 後建出元件鏈，以 error 物件為 key 暫存於 WeakMap，再由 event
// processor 依 hint.originalException 對回同一筆事件。SDK 的 captureException 包在 setTimeout 內，
// 必定晚於同步執行的本 hook，對應關係成立；WeakMap 不持有 error 的強參考，未被上報者隨 error 一起回收。
// 但只知道「哪個元件炸掉」還不夠：Vue 的 patch 一旦中途拋錯，`instance.subTree` 已先被指向新的 vnode 樹，
// 其中尚未 patch/mount 到的節點會永遠停在 component / el 為 null 的狀態，之後每次 re-render 或 unmount
// 都再拋一次（CLIENT-PWA-SSR-C1~C5 即為此類）。也就是說上報到的絕大多數是「連鎖崩潰」，真正的成因是該
// 次頁面載入中最早拋出的那一顆，而它可能被歸到完全不同的 issue。因此另外記錄本次頁面載入的 Vue 錯誤序號
// 與第一顆錯誤的來源，讓 triage 能以 `vueErrorSeq:1` 篩出成因、並從連鎖事件直接回指第一顆的元件。
const MAX_COMPONENT_TRACE_DEPTH = 10
const ANONYMOUS_COMPONENT_NAME = 'Anonymous'
// 取不到 instance（沒有元件鏈可算）與「有元件鏈但整條未具名」是不同的診斷事實，不可共用 Anonymous，
// 否則依 vueFirstErrorComponent 篩選時會混進毫無資訊的事件
const UNKNOWN_COMPONENT_NAME = 'Unknown'
const ROOT_COMPONENT_NAME = 'Root'
const TRUNCATED_TRACE_MARKER = '…'
// 序號只用來分辨「第一顆」與「其後的連鎖」，大數值本身無 triage 價值；逐一上報會讓 tag 基數隨連鎖長度
// 失控（單一 session 可累積數十顆），故超過上限一律併成同一個值
const MAX_EXACT_ERROR_SEQ = 9
const OVERFLOW_ERROR_SEQ = `${MAX_EXACT_ERROR_SEQ + 1}+`
// PWA 的 SPA session 可存活數十分鐘，只靠整頁重載歸零會把數十分鐘後才出現的「新成因」誤標成連鎖。
// 改以靜默間隔切分：實測連鎖每次 re-render / 導頁就復發一次（間隔以毫秒至秒計），一分鐘不再出現即
// 視為上一輪已結束，取此值可避免切斷真實連鎖，又不讓不相干的錯誤被串在一起。
const ERROR_CHAIN_IDLE_RESET_MS = 60_000

const formatComponentName = (vm) => {
  if (vm.$root === vm) return ROOT_COMPONENT_NAME

  // 正式 build 只保留 SFC 編譯注入的 __name（__file 為 dev-only）
  return vm.$options?.name || vm.$options?.__name || ANONYMOUS_COMPONENT_NAME
}

const buildComponentTrace = (instance) => {
  const trace = []
  let vm = instance

  while (vm && trace.length < MAX_COMPONENT_TRACE_DEPTH) {
    trace.push(formatComponentName(vm))
    vm = vm.$parent
  }

  // 未走到 Root 就到深度上限時標示截斷，避免把短鏈誤讀為完整元件鏈
  if (vm) trace.push(TRUNCATED_TRACE_MARKER)

  return trace
}

// 葉節點未具名時取最近的具名祖先，避免 tag 退化成無鑑別度的 Anonymous
const resolveNamedComponent = (componentTrace) =>
  componentTrace.find((name) => name !== ANONYMOUS_COMPONENT_NAME && name !== TRUNCATED_TRACE_MARKER) || ANONYMOUS_COMPONENT_NAME

const formatErrorSeq = (seq) => (seq > MAX_EXACT_ERROR_SEQ ? OVERFLOW_ERROR_SEQ : String(seq))

export default defineNuxtPlugin((nuxtApp) => {
  const diagnosticsByError = new WeakMap()
  // 目前這一輪連鎖累計的 Vue 錯誤數與第一顆錯誤的來源。只存字串，不持有 error 或元件實例的參考，
  // 故不影響回收。
  let vueErrorCount = 0
  let firstVueError = null
  let lastVueErrorAt = 0

  nuxtApp.hook('vue:error', (error, instance) => {
    if (!error || typeof error !== 'object') return

    const now = Date.now()
    if (now - lastVueErrorAt > ERROR_CHAIN_IDLE_RESET_MS) {
      vueErrorCount = 0
      firstVueError = null
    }
    lastVueErrorAt = now

    // 連鎖中的每一顆都要計數，含取不到 instance 者，否則序號無法代表真正的先後
    vueErrorCount += 1

    try {
      let componentTrace = null
      try {
        if (instance) componentTrace = buildComponentTrace(instance)
      } catch {
        // 異常的 Vue instance getter：元件鏈留 null，但序號與第一顆錯誤仍要記錄，
        // 否則第一顆會整筆沒有 vueErrorSeq，下一顆還會把自己記成成因，整輪都查不到 vueErrorSeq:1
      }

      if (!firstVueError) {
        firstVueError = {
          type: error.name || 'Error',
          component: componentTrace ? resolveNamedComponent(componentTrace) : null,
          trace: componentTrace?.join(' < ') || null,
        }
      }

      // 同一個 error 物件若沿 errorCaptured 鏈再次流經本 hook，序號要保留最早的那次：
      // SDK 的 captureException 晚於本 hook，覆寫會讓已在途的事件被標成連鎖而漏出 vueErrorSeq:1
      if (!diagnosticsByError.has(error)) {
        diagnosticsByError.set(error, { componentTrace, seq: vueErrorCount, firstError: firstVueError })
      }
    } catch {
      // 元件鏈屬加值診斷資訊，取不到時維持 SDK 原本的上報內容
    }
  })

  Sentry.getGlobalScope().addEventProcessor((event, hint) => {
    try {
      const originalException = hint?.originalException
      const diagnostics = originalException && typeof originalException === 'object' ? diagnosticsByError.get(originalException) : null
      if (!diagnostics) return event

      const { componentTrace, seq, firstError } = diagnostics

      // 連鎖崩潰與其成因在 Sentry 上多半是不同 issue，序號讓 triage 能直接以 vueErrorSeq:1 篩出成因
      event.tags = { ...event.tags, vueErrorSeq: formatErrorSeq(seq) }

      // 第一顆本身無「前一顆」可指，只有連鎖事件需要回指成因。
      // firstError 為同一物件被多筆事件共用，淺拷貝後才掛上去，避免後續 processor 就地修改而汙染其他事件
      if (seq > 1 && firstError) {
        event.contexts = { ...event.contexts, vue: { ...event.contexts?.vue, firstError: { ...firstError } } }
        event.tags = { ...event.tags, vueFirstErrorComponent: firstError.component || UNKNOWN_COMPONENT_NAME }
      }

      if (!componentTrace?.length) return event

      event.contexts = {
        ...event.contexts,
        vue: { ...event.contexts?.vue, componentName: componentTrace[0], trace: componentTrace.join(' < ') },
      }
      // context 無法被 Sentry 搜尋，元件名稱另存一份 tag 以便直接依元件查詢與分群
      event.tags = { ...event.tags, vueComponent: resolveNamedComponent(componentTrace) }
    } catch {
      // 加值失敗不得影響原始事件送出
    }

    return event
  })

  return {
    provide: {
      sentry: Sentry,
    },
  }
})
